import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  User, 
  Heart, 
  Eye, 
  ChefHat, 
  Save, 
  FileText,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Play
} from 'lucide-react';
import { Link } from 'react-router-dom';

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: User,
      title: "Welcome to Desi Pet Recipes! 🐾",
      description: "Create healthy, homemade meals for your pets using Indian ingredients",
      content: (
        <div className="text-center space-y-4">
          <div className="bg-gradient-to-r from-green-500 to-orange-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <p className="text-gray-600 leading-relaxed">
            Join thousands of pet parents creating nutritious meals using AAFCO nutrient guidelines with affordable Indian ingredients like dal, rice, and vegetables.
          </p>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-green-800 font-medium">🎉 Start your journey to healthier, happier pets!</p>
          </div>
        </div>
      ),
      action: "Let's Get Started"
    },
    {
      icon: User,
      title: "Step 1: Sign In",
      description: "Create your free account to access personalized features",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <User className="w-8 h-8 text-white" />
          </div>
          <ul className="text-left space-y-3">
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Quick sign-up in under 30 seconds</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Access all platform features - 100% FREE</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>Save unlimited recipes forever!</span>
            </li>
          </ul>
        </div>
      ),
      action: "Sign Up Now"
    },
    {
      icon: Heart,
      title: "Step 2: Add Your Pet",
      description: "Tell us about your furry friend for personalized nutrition",
      content: (
        <div className="space-y-4">
          <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600">We'll need some basic information:</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="font-medium text-gray-800">🐕 Pet Type</div>
              <div className="text-gray-600">Dog or Cat</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="font-medium text-gray-800">⚖️ Weight</div>
              <div className="text-gray-600">Current weight in kg</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="font-medium text-gray-800">🏃 Activity Level</div>
              <div className="text-gray-600">Low, Moderate, High</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="font-medium text-gray-800">🧬 Breed</div>
              <div className="text-gray-600">Breed information</div>
            </div>
          </div>
        </div>
      ),
      action: "Create Pet Profile"
    },
    {
      icon: Eye,
      title: "Step 3: View Daily Nutrition",
      description: "See your pet's specific AAFCO-compliant nutrition requirements",
      content: (
        <div className="space-y-4">
          <div className="bg-purple-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <Eye className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600">
            Based on your pet's profile, we'll calculate their daily nutritional needs using AAFCO nutrient guidelines.
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-purple-800 font-medium mb-2">You'll see:</div>
            <ul className="text-purple-700 text-sm space-y-1">
              <li>• Daily calorie requirements</li>
              <li>• Protein, fat, and carbohydrate needs</li>
              <li>• Essential vitamin and mineral requirements</li>
              <li>• Feeding frequency recommendations</li>
            </ul>
          </div>
        </div>
      ),
      action: "Check Nutrition"
    },
    {
      icon: ChefHat,
      title: "Step 4: Build Perfect Recipes",
      description: "Mix and match Indian ingredients until you get 'AAFCO Compliant' ✅",
      content: (
        <div className="space-y-4">
          <div className="bg-orange-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <ChefHat className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600">
            Our Recipe Builder makes it easy! Just keep adding ingredients you have at home:
          </p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Badge className="bg-yellow-100 text-yellow-800">🍛 Basmati Rice</Badge>
            <Badge className="bg-green-100 text-green-800">🌾 Moong Dal</Badge>
            <Badge className="bg-red-100 text-red-800">🍗 Chicken</Badge>
            <Badge className="bg-orange-100 text-orange-800">🥕 Carrots</Badge>
            <Badge className="bg-blue-100 text-blue-800">🐟 Fish</Badge>
            <Badge className="bg-purple-100 text-purple-800">🥬 Spinach</Badge>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-green-800 font-medium text-center">
              Keep adding until you see: ✅ "AAFCO-based Recipe"
            </p>
          </div>
        </div>
      ),
      action: "Start Building"
    },
    {
      icon: Save,
      title: "Step 5: Save Your Recipe",
      description: "Save unlimited recipes - Completely FREE forever!",
      content: (
        <div className="space-y-4">
          <div className="bg-pink-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <Save className="w-8 h-8 text-white" />
          </div>
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
            <div className="text-center space-y-3">
              <div className="text-3xl">🎉</div>
              <div className="font-bold text-green-800 text-xl">100% FREE - Unlimited Access!</div>
              <ul className="text-green-700 space-y-2 text-left max-w-md mx-auto">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Save <strong>unlimited recipes</strong> for all your pets</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Export <strong>unlimited PDFs</strong> anytime</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Access all <strong>platform features</strong> for free</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Share recipes via WhatsApp, Email, Print</span>
                </li>
              </ul>
              <div className="bg-white rounded-lg p-3 border border-green-300 mt-4">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold text-green-700">No subscriptions.</span> No hidden costs. Just free pet nutrition forever! 🐕
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      action: "Save Recipes"
    },
    {
      icon: FileText,
      title: "Step 6: Export & Share",
      description: "Download, print, or share your recipes easily",
      content: (
        <div className="space-y-4">
          <div className="bg-indigo-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600 text-center">
            Once your recipe is saved, you can:
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
              <FileText className="w-5 h-5 text-indigo-600" />
              <span><strong>Export PDF:</strong> Download professional recipe cards</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
              <span className="w-5 h-5 text-center">📱</span>
              <span><strong>Share via WhatsApp:</strong> Send to family/vet easily</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
              <span className="w-5 h-5 text-center">🖨️</span>
              <span><strong>Print:</strong> Keep physical copies in kitchen</span>
            </div>
          </div>
        </div>
      ),
      action: "Export & Share"
    },
    {
      icon: CheckCircle,
      title: "You're Ready! 🎉",
      description: "Start creating healthy meals your pet will love",
      content: (
        <div className="text-center space-y-6">
          <div className="bg-gradient-to-r from-green-500 to-blue-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-gray-800">
              Congratulations! You're all set up! 🎊
            </h3>
            <p className="text-gray-600">
              You now know exactly how to create nutritious meals using AAFCO nutrient guidelines for your pet with affordable Indian ingredients.
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 font-medium">
              🚀 Ready to start? Create your first pet profile now!
            </p>
          </div>
        </div>
      ),
      action: "Start Creating"
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete?.();
      onClose();
    } else {
      setCurrentStep(curr => curr + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(curr => curr - 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between mb-4">
            <Badge variant="outline" className="text-sm">
              Step {currentStep + 1} of {steps.length}
            </Badge>
            <div className="flex gap-1">
              {steps.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index <= currentStep ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
          <DialogTitle className="text-2xl text-center">
            {currentStepData.title}
          </DialogTitle>
          <DialogDescription className="text-center text-lg">
            {currentStepData.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {currentStepData.content}
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={handlePrev}
            disabled={isFirstStep}
            className="flex-1"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <Button onClick={handleNext} className="flex-1 bg-gradient-to-r from-green-500 to-orange-500 hover:from-green-600 hover:to-orange-600">
            {isLastStep ? (
              <>
                <Play className="w-4 h-4 mr-2" />
                {currentStepData.action}
              </>
            ) : (
              <>
                {currentStepData.action}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        <div className="text-center pt-2">
          <Button variant="ghost" onClick={onClose} className="text-sm text-gray-500">
            Skip for now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;